// @ts-strict-ignore
import * as React from 'react';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { FeatureItem, FixRequest, FixRequestStatusType } from '../../types';
import { ReleaseComponentProps } from '../../typescriptHelpers';
import { RootState } from '../../reducers/reducers';
import { selectIsTriageHiddenForApp, selectUser } from '../../selectors/selectors';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { GetFixRequestAuthorIds } from '../../helpers';
import routes from '../../routes';
import { antGrey, runwayColors } from '../styles/globalStyles';
import {
    EyeInvisibleOutlined,
    InfoCircleOutlined,
    ToolOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InboxOutlined,
    EditOutlined,
    ClockCircleOutlined,
    EllipsisOutlined,
    LoadingOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { globalMessage } from '../../utils/globalMessage';
import UpdateFixDetailsModal from './UpdateFixDetailsModal';
import Dropdown from 'antd/lib/dropdown';
import Modal from 'antd/lib/modal';
import { MenuProps } from 'antd/lib/menu';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { IgnoreUnignoreFRItemRequestItem, tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useError, useIsLoading, useIsRequiredFixApprover, useUpdated } from '@/utils/hooks/helpers';
import { consumeNotificationActionFromURL } from '@/helpers/notificationActionHelpers';

type FixRequestNotificationAction = 'approve' | 'reject';

const consumeFixActionFromURL = (fixRequestId: string): FixRequestNotificationAction | null => {
    const action = consumeNotificationActionFromURL('selectedFix', 'fixAction', (id) => id === fixRequestId)?.action;
    return action === 'approve' || action === 'reject' ? action : null;
};

const fixRequestToastKeysByAction = {
    approveSuccess: new Set<string>(),
    approveError: new Set<string>(),
    rejectSuccess: new Set<string>(),
    rejectError: new Set<string>(),
    pendingSuccess: new Set<string>(),
    pendingError: new Set<string>(),
};

export const getFeatureItemActions = (
    requiresFixRequestsApprovals: boolean,
    isItemIgnored: boolean,
    canAddFixRequestForFeatureItem: boolean,
    isReleaseKickedOff: boolean,
    isReleaseSubmitted: boolean,
    shouldPreventFixRequests: boolean,
    fixRequest?: FixRequest,
    canApproveReject?: boolean,
    currentUserAlreadyApproved?: boolean,
    canIgnoreUnignore?: boolean,
    isTriageHidden?: boolean,
) => {
    if (!fixRequest) {
        const items: MenuProps['items'] = [
            {
                label: requiresFixRequestsApprovals ? 'Request approval for a fix' : 'Pull in as fix...',
                key: 'addFix',
                icon: <ToolOutlined style={{ color: antGrey.grey7 }} />,
                disabled:
                    !canAddFixRequestForFeatureItem ||
                    !isReleaseKickedOff ||
                    (isReleaseSubmitted && shouldPreventFixRequests),
            },
            {
                label: isItemIgnored ? 'Include' : 'Ignore',
                key: 'includeIgnore',
                icon:
                    isItemIgnored ?
                        <EyeOutlined style={{ color: antGrey.grey7 }} />
                    :   <EyeInvisibleOutlined style={{ color: antGrey.grey7 }} />,

                disabled: !canIgnoreUnignore,
            },
        ];

        return items;
    } else {
        // Fixes that originated from a triage issue carry a triageIssueId; offer a deep-link back to that issue.
        const jumpToTriageItem: MenuProps['items'] =
            fixRequest.triageIssueId && !isTriageHidden ?
                [
                    {
                        label: 'Jump to issue in Triage',
                        key: 'jumpToTriage',
                        icon: <InboxOutlined style={{ color: runwayColors.primary }} />,
                    },
                ]
            :   [];
        const fixRequiresApprovals = fixRequest.fixApprovalSettings?.requiresApprovals ?? requiresFixRequestsApprovals;
        if (fixRequiresApprovals) {
            const items: MenuProps['items'] = [
                ...(fixRequest.status?.status !== FixRequestStatusType.FixRequestApproved ?
                    [
                        {
                            label: 'Approve fix',
                            key: 'approveFix',
                            icon: <CheckCircleOutlined style={{ color: runwayColors.statusGreen }} />,
                            disabled: !canApproveReject || currentUserAlreadyApproved,
                        },
                    ]
                :   []),
                ...(fixRequest.status?.status !== FixRequestStatusType.FixRequestRejected ?
                    [
                        {
                            label: 'Reject fix',
                            key: 'rejectFix',
                            icon: <CloseCircleOutlined style={{ color: runwayColors.statusRed }} />,
                            disabled: !canApproveReject,
                        },
                    ]
                :   []),
                ...((
                    fixRequest.status?.status === FixRequestStatusType.FixRequestApproved ||
                    fixRequest.status?.status === FixRequestStatusType.FixRequestRejected
                ) ?
                    [
                        {
                            label:
                                fixRequest.status.status === FixRequestStatusType.FixRequestApproved ?
                                    'Rescind approval'
                                :   'Rescind rejection',
                            key: 'moveFixToPending',
                            icon: <ClockCircleOutlined style={{ color: runwayColors.statusYellow }} />,
                        },
                    ]
                :   []),
                {
                    label: 'Edit fix details',
                    key: 'editFixDetails',
                    icon: <EditOutlined style={{ color: antGrey.grey7 }} />,
                },
                ...jumpToTriageItem,
                {
                    label: isItemIgnored ? 'Include' : 'Ignore',
                    key: 'includeIgnore',
                    icon:
                        isItemIgnored ?
                            <EyeOutlined style={{ color: antGrey.grey7 }} />
                        :   <EyeInvisibleOutlined style={{ color: antGrey.grey7 }} />,

                    disabled: !canIgnoreUnignore,
                },
            ];

            return items;
        } else {
            const items: MenuProps['items'] = [
                {
                    label: 'Edit fix details',
                    key: 'editFixDetails',
                    icon: <EditOutlined style={{ color: antGrey.grey7 }} />,
                },
                ...jumpToTriageItem,
                {
                    label: isItemIgnored ? 'Include' : 'Ignore',
                    key: 'includeIgnore',
                    icon: <InfoCircleOutlined style={{ color: runwayColors.info }} />,
                    disabled: !canIgnoreUnignore,
                },
            ];
            return items;
        }
    }
};

interface FeatureItemActionsProps extends FeatureItem, ReleaseComponentProps {
    workingBranchEnabled: boolean;
    requiresFixRequestsApprovals: boolean;
    canAddFixRequestForFeatureItem: boolean;
    onClickAddFixRequest?: (workItemId: string) => void;
    isReleaseKickedOff: boolean;
    isReleaseSubmitted: boolean;
    shouldPreventFixRequests: boolean;
    stepRunId: string;
}

const FeatureItemActions: React.FC<FeatureItemActionsProps> = (props: FeatureItemActionsProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const canApproveRejectRole = useUserCanPerformAction(
        RunwayUserAction.UAUpdateFixRequestStatus,
        undefined,
        undefined,
        undefined,
        undefined,
        props.appId,
    );
    const currentUser = useSelector(selectUser);
    const isTriageHidden = useSelector((state: RootState) => selectIsTriageHiddenForApp(state, props.appId));
    const isRequiredApprover = useIsRequiredFixApprover(props.orgId, props.appId);
    const canApproveReject = canApproveRejectRole && isRequiredApprover;
    const canIgnoreUnignore = isRequiredApprover;

    const currentUserAlreadyApproved = React.useMemo(() => {
        if (!props.fixRequest?.status) return false;
        const approverIds = GetFixRequestAuthorIds(props.fixRequest.status);
        return approverIds.includes(currentUser.id);
    }, [props.fixRequest?.status, currentUser.id]);

    const featureItem: FeatureItem = {
        id: props.id,
        ticket: props.ticket,
        code: props.code,
        explanationText: props.explanationText,
        ignored: props.ignored,
        autoIgnored: props.autoIgnored,
        isItemDone: props.isItemDone,
        isInLatestOrSelectedStagingBuild: props.isInLatestOrSelectedStagingBuild,
        isInLatestWorkingBranchBuild: props.isInLatestWorkingBranchBuild,
        debugVariant: props.debugVariant,
        associatedStabilityIssues: props.associatedStabilityIssues,
        fixRequest: props.fixRequest,
    };

    const ignoreIsLoading = useIsLoading(ReleaseStepUpdatable.IgnoreFRItem, props.id);
    const ignoreError = useError(ReleaseStepUpdatable.IgnoreFRItem, props.id);

    const unignoreIsLoading = useIsLoading(ReleaseStepUpdatable.UnignoreFRItem, props.id);
    const unignoreError = useError(ReleaseStepUpdatable.UnignoreFRItem, props.id);

    const approveFixRequestIsLoading = useIsLoading(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);
    const approveFixRequestSuccess = useUpdated(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);
    const approveFixRequestError = useError(ReleaseStepUpdatable.ApproveFixRequest, props.fixRequest?.id);

    const rejectFixRequestIsLoading = useIsLoading(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);
    const rejectFixRequestSuccess = useUpdated(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);
    const rejectFixRequestError = useError(ReleaseStepUpdatable.RejectFixRequest, props.fixRequest?.id);

    const moveFixRequestToPendingApprovalIsLoading = useIsLoading(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );
    const moveFixRequestToPendingApprovalSuccess = useUpdated(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );
    const moveFixRequestToPendingApprovalError = useError(
        ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
        props.fixRequest?.id,
    );
    const fixRequestId = props.fixRequest?.id;

    const [isFeatureItemActionLoading, setIsFeatureItemActionLoading] = React.useState(false);
    const [showEditFixDetailsModal, setShowEditFixDetailsModal] = React.useState(false);

    const isIgnored = props.ignored;

    const tryIgnoreUnignoreItem = (ignore: boolean) => {
        const type = ignore ? ReleaseStepUpdatable.IgnoreFRItem : ReleaseStepUpdatable.UnignoreFRItem;

        const items: IgnoreUnignoreFRItemRequestItem[] =
            props.groupedCodeItems ?
                props.groupedCodeItems.map((item) => ({
                    frItemId: item.id,
                    isIgnore: ignore,
                    isAutoIgnoreItem: item.autoIgnored,
                }))
            :   [{ frItemId: props.id, isIgnore: ignore, isAutoIgnoreItem: props.autoIgnored }];

        dispatch(
            tryUpdateReleaseStep(
                type,
                {
                    orgId: props.orgId,
                    appId: props.appId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    items: items,
                },
                props.id,
            ),
        );
    };

    const tryApproveFixRequest = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.ApproveFixRequest,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    const tryRejectFixRequest = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.RejectFixRequest,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    React.useEffect(() => {
        const fixRequest = props.fixRequest;
        if (!fixRequest?.id) {
            return;
        }
        const action = consumeFixActionFromURL(fixRequest.id);
        if (!action) {
            return;
        }
        if (!canApproveReject) {
            globalMessage.error('You do not have permission to approve or reject this fix request.');
            return;
        }
        if (action === 'approve') {
            if (fixRequest.status?.status === FixRequestStatusType.FixRequestApproved) {
                globalMessage.info('This fix request is already approved.');
                return;
            }
            if (currentUserAlreadyApproved) {
                globalMessage.info('You have already approved this fix request.');
                return;
            }
        } else if (fixRequest.status?.status === FixRequestStatusType.FixRequestRejected) {
            globalMessage.info('This fix request is already rejected.');
            return;
        }

        Modal.confirm({
            title: action === 'approve' ? 'Approve fix request?' : 'Reject fix request?',
            content: `You opened this fix request from a notification. Do you want to ${action} it now?`,
            icon: null,
            okText: action === 'approve' ? 'Approve' : 'Reject',
            okButtonProps: { danger: action === 'reject' },
            onOk() {
                if (action === 'approve') {
                    tryApproveFixRequest();
                } else {
                    tryRejectFixRequest();
                }
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.fixRequest?.id, canApproveReject]);

    const tryMoveFixRequestToPendingApproval = () => {
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.MoveFixRequestToPendingApproval,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                    frItemId: props.id,
                },
                {
                    fixId: props.fixRequest.id,
                    isAutoIgnoreItem: props.autoIgnored,
                },
                props.fixRequest?.id,
            ),
        );
    };

    React.useEffect(() => {
        if (
            ignoreIsLoading ||
            unignoreIsLoading ||
            approveFixRequestIsLoading ||
            rejectFixRequestIsLoading ||
            moveFixRequestToPendingApprovalIsLoading
        ) {
            setIsFeatureItemActionLoading(true);
        } else {
            setIsFeatureItemActionLoading(false);
        }
    }, [
        ignoreIsLoading,
        unignoreIsLoading,
        approveFixRequestIsLoading,
        rejectFixRequestIsLoading,
        moveFixRequestToPendingApprovalIsLoading,
        isFeatureItemActionLoading,
    ]);

    React.useEffect(() => {
        if (unignoreError) {
            globalMessage.error('Item include failed to update. Please try again');
        }
    }, [unignoreError]);

    React.useEffect(() => {
        if (ignoreError) {
            globalMessage.error('Item ignore failed to update. Please try again');
        }
    }, [ignoreError]);

    React.useEffect(() => {
        if (approveFixRequestIsLoading && fixRequestId) {
            fixRequestToastKeysByAction.approveSuccess.delete(fixRequestId);
            fixRequestToastKeysByAction.approveError.delete(fixRequestId);
        }
    }, [approveFixRequestIsLoading, fixRequestId]);

    React.useEffect(() => {
        if (approveFixRequestSuccess && fixRequestId && !fixRequestToastKeysByAction.approveSuccess.has(fixRequestId)) {
            fixRequestToastKeysByAction.approveSuccess.add(fixRequestId);
            globalMessage.success('Fix Request approved successfully!');
        }
    }, [approveFixRequestSuccess, fixRequestId]);

    React.useEffect(() => {
        if (approveFixRequestError && fixRequestId && !fixRequestToastKeysByAction.approveError.has(fixRequestId)) {
            fixRequestToastKeysByAction.approveError.add(fixRequestId);
            globalMessage.error(approveFixRequestError);
        }
    }, [approveFixRequestError, fixRequestId]);

    React.useEffect(() => {
        if (rejectFixRequestIsLoading && fixRequestId) {
            fixRequestToastKeysByAction.rejectSuccess.delete(fixRequestId);
            fixRequestToastKeysByAction.rejectError.delete(fixRequestId);
        }
    }, [rejectFixRequestIsLoading, fixRequestId]);

    React.useEffect(() => {
        if (rejectFixRequestSuccess && fixRequestId && !fixRequestToastKeysByAction.rejectSuccess.has(fixRequestId)) {
            fixRequestToastKeysByAction.rejectSuccess.add(fixRequestId);
            globalMessage.success('Fix Request rejected successfully!');
        }
    }, [rejectFixRequestSuccess, fixRequestId]);

    React.useEffect(() => {
        if (rejectFixRequestError && fixRequestId && !fixRequestToastKeysByAction.rejectError.has(fixRequestId)) {
            fixRequestToastKeysByAction.rejectError.add(fixRequestId);
            globalMessage.error(rejectFixRequestError);
        }
    }, [rejectFixRequestError, fixRequestId]);

    React.useEffect(() => {
        if (moveFixRequestToPendingApprovalIsLoading && fixRequestId) {
            fixRequestToastKeysByAction.pendingSuccess.delete(fixRequestId);
            fixRequestToastKeysByAction.pendingError.delete(fixRequestId);
        }
    }, [moveFixRequestToPendingApprovalIsLoading, fixRequestId]);

    React.useEffect(() => {
        if (
            moveFixRequestToPendingApprovalSuccess &&
            fixRequestId &&
            !fixRequestToastKeysByAction.pendingSuccess.has(fixRequestId)
        ) {
            fixRequestToastKeysByAction.pendingSuccess.add(fixRequestId);
            globalMessage.success('Fix Request moved to pending approval successfully!');
        }
    }, [moveFixRequestToPendingApprovalSuccess, fixRequestId]);

    React.useEffect(() => {
        if (
            moveFixRequestToPendingApprovalError &&
            fixRequestId &&
            !fixRequestToastKeysByAction.pendingError.has(fixRequestId)
        ) {
            fixRequestToastKeysByAction.pendingError.add(fixRequestId);
            globalMessage.error(moveFixRequestToPendingApprovalError);
        }
    }, [moveFixRequestToPendingApprovalError, fixRequestId]);

    const handleMenuClick: MenuProps['onClick'] = (e) => {
        switch (e.key) {
            case 'addFix': {
                props.onClickAddFixRequest(props.id);
                break;
            }
            case 'includeIgnore': {
                tryIgnoreUnignoreItem(!isIgnored);
                break;
            }
            case 'approveFix': {
                tryApproveFixRequest();
                break;
            }
            case 'rejectFix': {
                tryRejectFixRequest();
                break;
            }
            case 'moveFixToPending': {
                tryMoveFixRequestToPendingApproval();
                break;
            }
            case 'editFixDetails': {
                setShowEditFixDetailsModal(true);
                break;
            }
            case 'jumpToTriage': {
                if (props.fixRequest?.triageIssueId) {
                    history.push(
                        generatePath(routes.appTriageIssue, {
                            orgIdParam: props.orgId,
                            appIdParam: props.appId,
                            issueIdParam: props.fixRequest.triageIssueId,
                        }),
                    );
                }
                break;
            }
            default:
                break;
        }
    };

    const items = getFeatureItemActions(
        props.requiresFixRequestsApprovals,
        isIgnored,
        props.canAddFixRequestForFeatureItem,
        props.isReleaseKickedOff,
        props.isReleaseSubmitted,
        props.shouldPreventFixRequests,
        props.fixRequest,
        canApproveReject,
        currentUserAlreadyApproved,
        canIgnoreUnignore,
        isTriageHidden,
    );

    const menuProps = {
        items,
        onClick: handleMenuClick,
    };

    const isLoading =
        isFeatureItemActionLoading ||
        ignoreIsLoading ||
        approveFixRequestIsLoading ||
        rejectFixRequestIsLoading ||
        moveFixRequestToPendingApprovalIsLoading;

    return (
        <div>
            {showEditFixDetailsModal && props.fixRequest && (
                <UpdateFixDetailsModal
                    orgId={props.orgId}
                    appId={props.appId}
                    stepRunId={props.stepRunId}
                    releaseId={props.releaseId}
                    onCancel={() => setShowEditFixDetailsModal(false)}
                    fixRequest={props.fixRequest}
                    featureItemId={featureItem.id}
                />
            )}
            <Dropdown.Button
                menu={menuProps}
                type="link"
                buttonsRender={([_, rightButton]) => [null, rightButton]}
                icon={
                    isLoading ?
                        <LoadingOutlined style={{ color: antGrey.grey7 }} />
                    :   <EllipsisOutlined style={{ fontSize: '28px', color: antGrey.grey7 }} />
                }
                trigger={['click']}
            ></Dropdown.Button>
        </div>
    );
};

export default FeatureItemActions;
